<template>
  <div class="flex flex-col bg-black text-white h-[90dvh]">
    <main class="flex-grow flex items-center justify-center">
      <section
        class="relative w-full max-h-[90vh] aspect-video max-w-full object-contain"
        @click.capture="handleEarlyPreroll"
      >
        <video v-if="video" ref="videoPlayer"
           class="video-js vjs-default-skin w-full h-full object-cover"
           controls
           playsinline controlslist="nodownload" @contextmenu.prevent></video>
        <div v-if="showPrerollLoader" class="absolute inset-0 z-10 flex items-center justify-center bg-black">
          <div class="flex flex-col items-center gap-3">
            <div class="h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent"></div>
            <span class="text-sm tracking-wide">Loading ad...</span>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useVideoStore } from "../store/videoStore";
import { useOverlay } from "../composables/useOverlay";
import { getNextEpisode } from "../utils/episodeUtils";
import { useVideoAds } from "../composables/useVideoAds";
import { generateSlug } from "../utils/slugUtils";
import { useAdStore } from "../store/adStore";
import { analyticsEvent } from "../utils/analytics";
import { resolveSubtitleTracks, revokeSubtitleBlobUrls } from "../utils/subtitles";
import { consumeUserInitiatedPlayerEntry } from "../utils/playerAutoplayIntent";

import videojs from "video.js";
import "video.js/dist/video-js.css";


const defaultFeedId = import.meta.env.VITE_DEFAULT_FEED_ID;
const DELAY_NEXT_STEP = 2500;

const route = useRoute();
const router = useRouter();
const videoStore = useVideoStore();
const videoPlayer = ref(null);
const player = ref(null);
const video = ref(null);
const { showOverlay } = useOverlay();
const { initIMAAds, handleTimeUpdate, playPreRollAd } = useVideoAds(player, video, videoPlayer);
const adStore = useAdStore();
const autoPlay = ref(route.query.autoPlay === "true");
const activeBlobUrls = ref([]);
const MANAGED_TRACK_ATTR = "data-rhebus-managed-track";
const isWaitingForPreroll = ref(false);
const showPrerollLoader = ref(false);
const shouldRewindAfterPreroll = ref(false);
const isAwaitingManualPrerollStart = ref(false);
let imaInitializationPromise = Promise.resolve();
let activeAdsManager = null;
let activeAdsManagerStartedEventType = null;

function resolveAutoPlayIntent() {
  const hasAutoPlayQuery = route.query.autoPlay === "true";
  const hasUserInitiatedIntent = consumeUserInitiatedPlayerEntry();
  autoPlay.value = hasAutoPlayQuery || hasUserInitiatedIntent;
}

function startPrerollGate() {
  if (isWaitingForPreroll.value) return;
  isWaitingForPreroll.value = true;
  showPrerollLoader.value = true;
  shouldRewindAfterPreroll.value = true;
}

function clearPrerollGate() {
  isWaitingForPreroll.value = false;
  showPrerollLoader.value = false;
}

function handlePrerollStart() {
  if (player.value) {
    player.value.muted(false);
    player.value.volume?.(1);
  }
  if (!isWaitingForPreroll.value) return;
  clearPrerollGate();
}

function detachAdsListener() {
  if (!activeAdsManager || !activeAdsManagerStartedEventType) return;
  activeAdsManager.removeEventListener(
    activeAdsManagerStartedEventType,
    handlePrerollStart
  );
  activeAdsManager = null;
  activeAdsManagerStartedEventType = null;
}

async function handlePrerollBypass(reason) {
  if (!isWaitingForPreroll.value) return;
  shouldRewindAfterPreroll.value = false;
  clearPrerollGate();
  try {
    await player.value.play();
  } catch (err) {
    console.warn(`[PREROLL] Unable to resume content after ${reason}:`, err);
  }
}

function handleEarlyPreroll() {
  if (!isAwaitingManualPrerollStart.value) return;
  if (!player.value?.paused?.()) return;
  startPrerollGate();
  player.value.play().catch((err) => {
    console.warn("[PREROLL] Early gesture play failed:", err);
    clearPrerollGate();
  });
}

async function loadVideoBySlug(slug) {
  resolveAutoPlayIntent();

  const partnerId = route.params.partnerId;
  const feedId = partnerId || defaultFeedId;
  await videoStore.loadVideos(feedId);

  const allVideos = videoStore.allVideosAndEpisodes;
  video.value = videoStore.selectedVideo || allVideos.find((v) => generateSlug(v.title, v.id) === slug);

  if (!video.value) {
    router.push({ name: "404" });
  } else {
    await nextTick();
    await updateVideoSource();
  }
}

async function updateVideoSource() {

  if (!videoPlayer.value) {
    console.warn("❌ Video element not available in the DOM yet.");
    return;
  }
  if (!player.value) {
    player.value = videojs(videoPlayer.value, {
      autoplay: false,
      controls: true,
      html5: {
        nativeTextTracks: true
      },
      poster: video.value.thumbnail,
      bigPlayButton: true
    });

    const trackFirstVideoPlay = () => {
      if (video.value?.title) {
        analyticsEvent(video.value.title, "beacon", "video_start");
        player.value.off("play", trackFirstVideoPlay);
      }
    };
    player.value.on("play", trackFirstVideoPlay);
    player.value.off("ended");
    player.value.on("ended", handleVideoEnded);
    player.value.on("ads-manager", (event) => {
      const adsManager = event?.adsManager;
      if (!adsManager) return;

      detachAdsListener();
      activeAdsManager = adsManager;
      activeAdsManagerStartedEventType =
        globalThis?.google?.ima?.AdEvent?.Type?.STARTED || "started";
      activeAdsManager.addEventListener(
        activeAdsManagerStartedEventType,
        handlePrerollStart
      );
    });
    player.value.on("ads-ad-ended", () => {
      if (!shouldRewindAfterPreroll.value) return;
      shouldRewindAfterPreroll.value = false;
      player.value.currentTime(0);
    });
    player.value.on("adserror", () => handlePrerollBypass("ad error"));
    player.value.on("adtimeout", () => handlePrerollBypass("ad timeout"));
    player.value.on("nopreroll", () => handlePrerollBypass("no preroll"));
    
    player.value.on("ready", async () => {
      await imaInitializationPromise;
      if (autoPlay.value) {
        isAwaitingManualPrerollStart.value = false;
        await player.value.pause();
        shouldRewindAfterPreroll.value = true;
        await playPreRollAd();
        await player.value.play();
      } else {
        isAwaitingManualPrerollStart.value = true;
        player.value.one("play", async () => {
          isAwaitingManualPrerollStart.value = false;
          await player.value.pause();
          startPrerollGate();
          try {
            await playPreRollAd();
            await player.value.play();
          } catch (err) {
            console.warn("[PREROLL] Failed to play preroll:", err);
            clearPrerollGate();
            await player.value.play();
          }
        });
      }
      await setUpSubtitleTracks();
    });
  }
  

  const validAdsCacheUrls = adStore.getStoredAdsUrlsValid();
  imaInitializationPromise = initIMAAds(
    validAdsCacheUrls,
    autoPlay.value,
    true,
    video.value.content.adBreaks
  ).catch((error) => {
    console.warn("[IMA] Deferred initialization failed:", error);
  });
  await imaInitializationPromise;
  setVideoSource();

  player.value.off("timeupdate", handleTimeUpdate);
  player.value.on("timeupdate", handleTimeUpdate);
}

function setVideoSource() {
  if (!player.value || !video.value) return;

  player.value.src({
    src: video.value.url || video.value.content.videos[0].url,
    type: "video/mp4"
  });
  player.value.poster(video.value.thumbnail);
}

async function setUpSubtitleTracks() {
 // Resolve subtitle tracks asynchronously BEFORE changing the source so that
  // they can be registered synchronously right after player.src() — avoiding
  // the race condition where Video.js clears autoRemote tracks on source set.
  const subtitleRecords =
    video.value.subtitles ||
    video.value.content?.subtitles ||
    [];

  revokeSubtitleBlobUrls(activeBlobUrls.value);
  activeBlobUrls.value = [];

  let resolvedTracks = [];
  if (subtitleRecords.length > 0) {
    try {
      const { tracks, blobUrls } = await resolveSubtitleTracks(subtitleRecords);
      resolvedTracks = tracks;
      activeBlobUrls.value = blobUrls;
    } catch (err) {
      console.warn("[SUBTITLES] Failed to resolve subtitle tracks:", err);
    }
  }

   // Register new subtitle tracks synchronously right after source change so
  // Video.js fires addtrack events and shows the SubsCapsButton.
  const existing = player.value.remoteTextTracks();
  for (let i = existing.length - 1; i >= 0; i--) {
    player.value.removeRemoteTextTrack(existing[i]);
  }
  const previousManagedTracks = videoPlayer.value?.querySelectorAll?.(`track[${MANAGED_TRACK_ATTR}]`) || [];
  previousManagedTracks.forEach((trackEl) => trackEl.remove());

  resolvedTracks.forEach((track) => {
    const nativeTrackEl = document.createElement("track");
    nativeTrackEl.kind = track.kind || "subtitles";
    nativeTrackEl.src = track.src;
    nativeTrackEl.srclang = track.srclang || "en";
    nativeTrackEl.label = track.label || nativeTrackEl.srclang;
    nativeTrackEl.default = Boolean(track.default);
    nativeTrackEl.setAttribute(MANAGED_TRACK_ATTR, "true");
    videoPlayer.value?.appendChild(nativeTrackEl);
  });

}

function handleVideoEnded() {
  if (player.value.currentTime() < video.value.content.duration - 5) {
    return;
  }

  analyticsEvent(video.value.title, "beacon", "video_complete");

  player.value.off("ended");


  const { nextEpisode } = getNextEpisode(videoStore.videos, video.value, videoStore.selectedSection);
  if (nextEpisode) {
    showOverlay(player.value, `${nextEpisode.title}`);
    // Update browser history to reflect the next video
  }

  setTimeout(async () => {
    videoStore.selectVideo(nextEpisode);
    const newSlug = generateSlug(nextEpisode.title, nextEpisode.id);
    const partnerId = route.params.partnerId;
    await router.replace({
      name: "video-detail",
      params: { partnerId, slug: newSlug },
      query: { ...route.query }
    })
    if (nextEpisode) {
      autoPlay.value = true;
      analyticsEvent(nextEpisode?.title ?? video.value.title, "video_selection", "autoplay_video_start");
      router.push({
        name: "video-player",
        params: {
          partnerId: route.params.partnerId,
          slug: generateSlug(
            nextEpisode?.title ?? video.value.title,
            nextEpisode?.id ?? video.value.id
          ),

        },
        query: {
          ...route.query,
          autoPlay: true
        }
      });
      return;
    }
  }, DELAY_NEXT_STEP);
}

function handleVisibilityChange() {
  if (!document.hidden) {
    player.value?.ima?.getAdsManager?.()?.resume?.();
  }
}

watch(
  () => route.params.slug,
  async (newSlug) => {
    if (newSlug) {
      await loadVideoBySlug(newSlug);
    }
  }
);

onMounted(async () => {
  const videoSlug = route.params.slug;
  await loadVideoBySlug(videoSlug);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  adStore.setupRequestMethod(adStore.requestMethods.NULL);
  adStore.stopInterval();
});

onBeforeUnmount(() => {
  detachAdsListener();
  clearPrerollGate();
  revokeSubtitleBlobUrls(activeBlobUrls.value);
  activeBlobUrls.value = [];
  const previousManagedTracks = videoPlayer.value?.querySelectorAll?.(`track[${MANAGED_TRACK_ATTR}]`) || [];
  previousManagedTracks.forEach((trackEl) => trackEl.remove());

  if (player.value) {
    player.value.dispose();
  }
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  adStore.setupRequestMethod(adStore.requestMethods.HEAD);
  adStore.startInterval();
});
</script>

<style>
.vjs-poster {
  background-color: #000;
}
.vjs-poster img {
  object-fit: cover;
  background-color: #000;
}
</style>
